import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, TrendingUp, Wallet, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Altersvorsorge-Kategorien - 3-Töpfe-Modell
 * Struktur nach Zeithorizont: kurzfristig → mittelfristig → langfristig
 * 
 * Korrektur: Altersvorsorgedepot gehört in Topf 3 (langfristig gebunden),
 * nicht in Topf 2, da Auszahlung frühestens ab 85, kein flexibler Zugriff.
 */

const toepfe = [
  {
    id: 'topf1',
    nummer: '01',
    titel: 'Flexibel & kurzfristig verfügbar',
    untertitel: 'Liquiditätsreserve',
    icon: Wallet,
    farbe: 'slate',
    inhalt: 'Bevor es um Altersvorsorge geht, braucht es eine Basis: Geld, das jederzeit verfügbar ist. Für unvorhergesehene Ausgaben, für Flexibilität, für Sicherheit im Alltag. Das ist kein Wachstumstopf, sondern ein Puffer.',
    hinweis: 'Empfehlung: 3 bis 6 Monatsausgaben auf einem Tagesgeldkonto.',
    lizenz: null,
  },
  {
    id: 'topf2',
    nummer: '02',
    titel: 'Wachstumsorientiert, mittelfristig',
    untertitel: 'Flexibles Depot',
    icon: TrendingUp,
    farbe: 'slate',
    inhalt: 'Für Vermögensaufbau ohne Bindung. Ein ETF-Sparplan ist echt flexibel: Du kannst jederzeit einzahlen, pausieren oder Geld entnehmen. Keine staatliche Förderung, aber auch keine Einschränkungen.',
    details: [
      'ETF-Sparplan im Depot: Flexibel, jederzeit verfügbar, keine Förderung, keine Bindung',
    ],
    lizenz: null,
  },
  {
    id: 'topf3',
    nummer: '03',
    titel: 'Langfristig gebunden, bis zur Rente',
    untertitel: 'Altersvorsorge-Lösungen',
    icon: Shield,
    farbe: 'cobalt',
    inhalt: 'Für alle, die gezielt fürs Alter sparen wollen, mit steuerlichen Vorteilen oder staatlicher Förderung. Diese Produkte sind auf die Rentenphase ausgerichtet.',
    subsections: [
      {
        titel: 'Lösungen',
        details: [
          'Fondsgebundene Rentenversicherung: Fondswachstum im Versicherungsmantel, Steuervorteile bei Verrentung, oft mit Todesfallschutz kombinierbar',
          'Basisrente (Rürup): Für Selbstständige und Gutverdiener, hohe Steuerersparnis in der Ansparphase',
        ],
        lizenz: null,
      },
      {
        titel: 'Neues Altersvorsorgedepot (ab 2027)',
        details: [
          'Staatlich gefördert, aber ohne Garantie und ohne lebenslange Verrentungspflicht',
          'Auszahlung ab 65 möglich, flexibel gestaltbar, zum Beispiel als Auszahlungsplan bis 85. Keine Pflicht zur lebenslangen Verrentung, aber strukturell näher an einer Rentenversicherung als an einem frei verfügbaren Depot',
        ],
        zusatztext: 'Mit der Riester-Nachfolge entsteht hier gerade neues Potenzial. Deshalb lohnt sich jetzt eine unabhängige Einordnung, welches Modell zu dir passt.',
        lizenz: null,
      },
    ],
    hinweis: 'Die klassische Riester-Rentenversicherung wird durch das neue Altersvorsorgedepot weitgehend abgelöst (Riester-Reform, Bundestag Mai 2026, Umsetzung ab 1.1.2027). Bestehende Verträge laufen weiter.',
  },
];

export const AltersvorsorgeKategorien = () => {
  const navigate = useNavigate();

  return (
    <section className="py-16 md:py-24 bg-ice" data-testid="altersvorsorge-kategorien">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8">
        {/* Header */}
        <div className="mb-10">
          <h2 className="font-outfit text-lg sm:text-xl font-bold text-navy mb-3">
            Drei Töpfe für die Altersvorsorge
          </h2>
          <p className="text-sm sm:text-base text-charcoal leading-relaxed">
            Nicht jedes Produkt passt zu jedem Ziel. Eine sinnvolle Struktur beginnt mit der Frage: 
            Wofür ist das Geld gedacht und wann brauchst du es?
          </p>
        </div>

        {/* Töpfe als Stufen/Pyramide */}
        <div className="space-y-4">
          {toepfe.map((topf, index) => (
            <motion.div
              key={topf.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className={`rounded-xl p-5 sm:p-6 border ${
                topf.farbe === 'cobalt' 
                  ? 'bg-white border-cobalt/20' 
                  : 'bg-white border-black/5'
              }`}
              style={{
                marginLeft: `${index * 8}px`,
                marginRight: `${(2 - index) * 8}px`,
              }}
            >
              {/* Header */}
              <div className="flex items-start gap-3 mb-4">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  topf.farbe === 'cobalt' ? 'bg-cobalt/10' : 'bg-slate-100'
                }`}>
                  <topf.icon className={`w-4 h-4 ${
                    topf.farbe === 'cobalt' ? 'text-cobalt' : 'text-slate-500'
                  }`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`font-mono text-[10px] font-medium ${
                      topf.farbe === 'cobalt' ? 'text-cobalt' : 'text-slate-400'
                    }`}>
                      Topf {topf.nummer}
                    </span>
                    <span className="text-[10px] text-slate-300">|</span>
                    <span className="text-[10px] text-slate-500">{topf.untertitel}</span>
                  </div>
                  <h3 className="font-outfit text-base sm:text-lg font-bold text-navy">
                    {topf.titel}
                  </h3>
                </div>
              </div>

              {/* Inhalt */}
              <p className="text-sm text-charcoal leading-relaxed mb-3">
                {topf.inhalt}
              </p>

              {/* Details (für Topf 1 und 2) */}
              {topf.details && (
                <ul className="space-y-1.5 mb-3">
                  {topf.details.map((detail, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-charcoal">
                      <span className="text-cobalt mt-0.5">•</span>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Subsections (für Topf 3) */}
              {topf.subsections && (
                <div className="space-y-4 mt-4">
                  {topf.subsections.map((sub, i) => (
                    <div key={i} className={`p-4 rounded-lg ${
                      sub.lizenz?.typ === 'eigen' ? 'bg-cobalt/5' : 'bg-slate-50'
                    }`}>
                      <h4 className="font-outfit text-sm font-semibold text-navy mb-2">
                        {sub.titel}
                      </h4>
                      <ul className="space-y-1.5 mb-3">
                        {sub.details.map((detail, j) => (
                          <li key={j} className="flex items-start gap-2 text-xs text-charcoal leading-relaxed">
                            <span className={sub.lizenz?.typ === 'eigen' ? 'text-cobalt' : 'text-slate-400'}>•</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                      {sub.zusatztext && (
                        <p className="text-xs text-charcoal leading-relaxed mb-3 italic">
                          {sub.zusatztext}
                        </p>
                      )}
                      {sub.lizenz && (
                        <div className={`flex items-start gap-2 text-[10px] ${
                          sub.lizenz.typ === 'eigen' ? 'text-cobalt' : 'text-slate-500'
                        }`}>
                          {sub.lizenz.typ === 'eigen' ? (
                            <Shield className="w-3 h-3 flex-shrink-0 mt-0.5" />
                          ) : (
                            <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
                          )}
                          <span>{sub.lizenz.text}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Hinweis */}
              {topf.hinweis && (
                <p className="text-[10px] text-slate leading-relaxed mt-3">
                  {topf.hinweis}
                </p>
              )}

              {/* Lizenz-Badge (für Topf 1 und 2) */}
              {topf.lizenz && !topf.subsections && (
                <div className={`flex items-start gap-2 p-3 rounded-lg text-xs mt-3 ${
                  topf.lizenz.typ === 'eigen' 
                    ? 'bg-cobalt/5 border border-cobalt/10' 
                    : 'bg-slate-50 border border-slate-200'
                }`}>
                  {topf.lizenz.typ === 'eigen' ? (
                    <Shield className="w-3.5 h-3.5 text-cobalt flex-shrink-0 mt-0.5" />
                  ) : (
                    <Info className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
                  )}
                  <span className={topf.lizenz.typ === 'eigen' ? 'text-navy' : 'text-slate-600'}>
                    {topf.lizenz.text}
                  </span>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/strategiegespraech')}
            className="group inline-flex items-center gap-2 px-5 py-2.5 bg-navy text-white rounded-lg font-medium text-sm hover:bg-charcoal transition-all"
          >
            Einordnung besprechen
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* Wichtig-Hinweis */}
        <div className="mt-8 p-4 bg-white rounded-lg border border-black/5">
          <p className="text-xs text-charcoal leading-relaxed">
            <span className="font-medium text-navy">Wichtig:</span> Diese Übersicht zeigt dir, welche Optionen es gibt und wie sie sich unterscheiden. 
            Im persönlichen Gespräch klären wir, was zu deiner Situation passt.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AltersvorsorgeKategorien;
